import {
  GlobeIcon,
  RedditLogoIcon,
  ThreadsLogoIcon,
  TiktokLogoIcon,
  XLogoIcon,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import type React from 'react';

import { FacebookIcon } from '@/components/icons/FacebookIcon';
import { GoogleAdsIcon } from '@/components/icons/GoogleAdsIcon';
import { GoogleAnalyticsIcon } from '@/components/icons/GoogleAnalyticsIcon';
import { GoogleDriveIcon } from '@/components/icons/GoogleDriveIcon';
import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { InstagramIcon } from '@/components/icons/InstagramIcon';
import { LinkedInIcon } from '@/components/icons/LinkedInIcon';
import { MetaAdsIcon } from '@/components/icons/MetaAdsIcon';
import { PinterestIcon } from '@/components/icons/PinterestIcon';
import { ShopifyIcon } from '@/components/icons/ShopifyIcon';
import { SlackIcon } from '@/components/icons/SlackIcon';
import { YouTubeIcon } from '@/components/icons/YouTubeIcon';
import {
  type PlatformIdentityKey,
  getCanonicalPlatformKey,
  getPlatformBrandColorClass,
} from '@/lib/platforms/identity';
import { cn } from '@/lib/utils';

export type PlatformBrandIconProps = Omit<
  React.SVGProps<SVGSVGElement>,
  'color' | 'fill' | 'stroke' | 'style' | 'weight'
> & {
  /** The only non-brand presentation: use for disabled or unavailable platform choices. */
  tone?: 'brand' | 'muted';
};

export type PlatformBrandIconComponent = React.FC<PlatformBrandIconProps>;

type BrandMark = {
  Icon: PlatformBrandIconComponent;
};

function filledPhosphorMark(Icon: Icon): PlatformBrandIconComponent {
  return function FilledPhosphorMark(props: PlatformBrandIconProps) {
    return <Icon {...props} weight="fill" />;
  };
}

const SOURCE_ICONS: Record<PlatformIdentityKey, BrandMark> = {
  facebook: { Icon: FacebookIcon },
  'meta-ads': { Icon: MetaAdsIcon },
  'google-ads': { Icon: GoogleAdsIcon },
  'linkedin-ads': { Icon: LinkedInIcon },
  instagram: { Icon: InstagramIcon },
  linkedin: { Icon: LinkedInIcon },
  tiktok: { Icon: filledPhosphorMark(TiktokLogoIcon) },
  pinterest: { Icon: PinterestIcon },
  youtube: { Icon: YouTubeIcon },
  shopify: { Icon: ShopifyIcon },
  'google-analytics': { Icon: GoogleAnalyticsIcon },
  'google-business': { Icon: GoogleIcon },
  'google-drive': { Icon: GoogleDriveIcon },
  x: { Icon: filledPhosphorMark(XLogoIcon) },
  threads: { Icon: filledPhosphorMark(ThreadsLogoIcon) },
  reddit: { Icon: filledPhosphorMark(RedditLogoIcon) },
  slack: { Icon: SlackIcon },
};

/**
 * The key of the FIRST platform that draws the same mark as `platform`.
 * `linkedin` and `linkedin-ads` are two connections but one logo, so a cluster
 * of marks keyed by platform id draws the LinkedIn mark twice, side by side,
 * and reads as a rendering glitch. Dedupe a mark cluster on this key, not on
 * the platform id.
 */
export function getPlatformMarkKey(platform: unknown): string {
  const key = getCanonicalPlatformKey(platform);
  const mark = SOURCE_ICONS[key as PlatformIdentityKey];
  if (!mark) return key;
  const first = (Object.keys(SOURCE_ICONS) as PlatformIdentityKey[]).find(
    (candidate) => SOURCE_ICONS[candidate].Icon === mark.Icon,
  );
  return first ?? key;
}

const iconCache = new Map<string, PlatformBrandIconComponent>();

/**
 * Returns the sole platform-mark renderer for product UI. Badge-style brands
 * preserve their official white interior; Phosphor is used only for the marks
 * that are intentionally single-colour glyphs.
 */
export function getPlatformBrandIcon(platform: unknown): PlatformBrandIconComponent {
  const key = getCanonicalPlatformKey(platform);
  const cached = iconCache.get(key);
  if (cached) return cached;

  const mark = SOURCE_ICONS[key];
  const SourceIcon = mark?.Icon ?? GlobeIcon;
  const brandColor = mark ? getPlatformBrandColorClass(key) : 'text-muted-foreground';

  const PlatformBrandIcon = ({
    className,
    tone = 'brand',
    'aria-label': ariaLabel,
    'aria-hidden': ariaHidden,
    ...props
  }: PlatformBrandIconProps) => (
    <SourceIcon
      {...props}
      aria-hidden={ariaHidden ?? (ariaLabel ? undefined : true)}
      aria-label={ariaLabel}
      className={cn(className, tone === 'muted' ? 'text-muted-foreground' : brandColor)}
      data-platform-brand-mark={key || 'unknown'}
    />
  );
  PlatformBrandIcon.displayName = `PlatformBrandIcon(${key || 'unknown'})`;
  iconCache.set(key, PlatformBrandIcon);
  return PlatformBrandIcon;
}

export function PlatformBrandIcon({
  platform,
  ...props
}: PlatformBrandIconProps & { platform: unknown }): React.JSX.Element {
  const Icon = getPlatformBrandIcon(platform);
  return <Icon {...props} />;
}
